import React from 'react';
import { Link } from 'react-router-dom';

// Trust badges component for conversion optimization
const TrustBadges = ({ variant = 'default' }) => {
  const badges = [
    { icon: '✓', text: 'Licensed & Insured', color: 'green' },
    { icon: '✓', text: 'Eco-Friendly', color: 'green' },
    { icon: '✓', text: '24/7 Available', color: 'green' },
    { icon: '✓', text: 'Satisfaction Guaranteed', color: 'green' }
  ];

  if (variant === 'compact') {
    return (
      <div className="flex flex-wrap justify-center gap-4 text-sm">
        {badges.map((badge, i) => (
          <div key={i} className="flex items-center text-gray-600">
            <span className="text-green-500 mr-1">{badge.icon}</span>
            <span>{badge.text}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="bg-gray-50 py-4 border-y">
      <div className="container mx-auto px-4">
        <div className="flex flex-wrap justify-center items-center gap-6 md:gap-12">
          {badges.map((badge, i) => (
            <div key={i} className="flex items-center">
              <span className="bg-green-100 text-green-600 p-1 rounded-full mr-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
              </span>
              <span className="font-medium text-gray-700">{badge.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Internal link component for SEO
export const InternalLink = ({ to, children, className = '' }) => (
  <Link 
    to={to} 
    className={`text-green-600 hover:text-green-700 hover:underline font-medium ${className}`}
  >
    {children}
  </Link>
);

// Related services component for internal linking
export const RelatedServices = ({ currentSlug, services }) => {
  const relatedServices = services
    .filter(s => s.slug !== currentSlug)
    .slice(0, 4);

  if (relatedServices.length === 0) return null;

  return (
    <div className="bg-gray-50 p-6 rounded-lg mt-8">
      <h3 className="text-lg font-bold mb-4">Related Services</h3>
      <div className="grid grid-cols-2 gap-3">
        {relatedServices.map(service => (
          <Link 
            key={service.slug}
            to={`/services/${service.slug}`}
            className="text-sm text-gray-700 hover:text-green-600 flex items-center"
          >
            <span className="text-green-500 mr-2">→</span>
            {service.name}
          </Link>
        ))}
      </div>
    </div>
  );
};

// Quick contact widget
export const QuickContact = ({ location = '' }) => (
  <div className="bg-gradient-to-br from-green-600 to-green-700 text-white p-6 rounded-lg shadow-lg">
    <h3 className="text-xl font-bold mb-2">Need Help Now?</h3>
    <p className="text-white/90 text-sm mb-4">
      {location ? `Serving ${location} 24/7` : 'Fast response, guaranteed results'}
    </p>
    <a 
      href="tel:6462099955" 
      className="block bg-white text-green-600 text-center py-3 rounded-lg font-bold mb-3 hover:bg-gray-100 transition"
    >
      <span className="flex items-center justify-center">
        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
          <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
        </svg>
        (646) 209-9955
      </span>
    </a>
    <Link 
      to="/contact" 
      className="block bg-blue-600 text-center py-3 rounded-lg font-bold hover:bg-blue-700 transition"
    >
      Get Free Quote
    </Link>
  </div>
);

// Service area links for internal linking
export const ServiceAreaLinks = ({ currentSlug }) => {
  const areas = [
    { name: 'Queens', slug: 'queens-ny' },
    { name: 'Manhattan', slug: 'manhattan-ny' },
    { name: 'Brooklyn', slug: 'brooklyn-ny' },
    { name: 'Bronx', slug: 'bronx-ny' },
    { name: 'Long Island', slug: 'long-island-ny' }
  ].filter(a => a.slug !== currentSlug);

  return (
    <div className="mt-6">
      <h4 className="text-sm font-semibold text-gray-500 mb-2">Also Serving:</h4>
      <div className="flex flex-wrap gap-2">
        {areas.map(area => (
          <Link 
            key={area.slug}
            to={`/service-areas/${area.slug}`}
            className="text-xs bg-gray-100 text-gray-700 px-3 py-1 rounded-full hover:bg-green-100 hover:text-green-700 transition"
          >
            {area.name}
          </Link>
        ))}
      </div>
    </div>
  );
};

export default TrustBadges;
